import React, { useEffect, useState, useMemo } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import { BiSolidEdit } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import {
  createCommentForPartner,
  deleteComment,
  updateComment,
  getCommentsForPartner,
} from "../redux/apiCalls/accidentApiCall";
import { ClipLoader } from "react-spinners";  
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

function AdminNotizenPartner() {
  const dispatch = useDispatch();
  const { partner, getLoading, error } = useSelector((state) => state.accident);
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [editableNoteId, setEditableNoteId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // جلب البيانات عند تحميل الصفحة
  useEffect(() => {
    if (partner?.user?._id) {
      dispatch(getCommentsForPartner(partner.user._id));
    }
  }, [dispatch, partner?.user?._id]);

  // دالة لإضافة أو تحديث ملاحظة
  const handleAddOrUpdateComment = async () => {
    if (!text.trim() || !title.trim()) {
      alert("Both title and comment are required!");
      return;
    }

    if (editableNoteId) {
      await dispatch(updateComment(editableNoteId, text, title, "partner"));
    } else {
      await dispatch(createCommentForPartner(text, partner.user._id, title));
    }

    // إعادة جلب البيانات بعد التعديل
    await dispatch(getCommentsForPartner(partner.user._id));
    resetModal();
  };

  // دالة لمسح القيم بعد الإضافة أو التعديل
  const resetModal = () => {
    setText("");
    setTitle("");
    setEditableNoteId(null);
    setIsModalOpen(false);
  };

  // دالة لتحويل التاريخ إلى الصيغة المطلوبة
  const renderDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getUTCDate().toString().padStart(2, "0")}.${(
      date.getUTCMonth() + 1
    )
      .toString()
      .padStart(2, "0")}.${date.getUTCFullYear()}`;
  };

  // تعريف أعمدة الجدول
  const columns = useMemo(
    () => [
      {
        header: "Betreff",
        accessorKey: "title",
      },
      {
        header: "Datum",
        accessorKey: "createdAt",
        cell: ({ getValue }) => renderDate(getValue()),
      },
      {
        header: "Aktion",
        accessorKey: "_id",
        cell: ({ row }) => (
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => {
                dispatch(deleteComment(row.original._id, "partner")).then(() =>
                  dispatch(getCommentsForPartner(partner.user._id))
                );
              }}
              className="text-red-500 hover:text-red-700"
            >
              <RiDeleteBin6Line className="text-2xl" />
            </button>
            <button
              onClick={() => {
                setText(row.original.text);
                setTitle(row.original.title);
                setEditableNoteId(row.original._id);
                setIsModalOpen(true);
              }}
              className="text-blue-500 hover:text-blue-700"
            >
              <BiSolidEdit className="text-2xl" />
            </button>
          </div>
        ),
      },
    ],
    [dispatch, partner?.user?._id]
  );

  // استخدام useReactTable
  const table = useReactTable({
    data: partner?.user?.notes || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto p-4">
      {/* تحميل أو رسالة خطأ */}
      {getLoading ? (
        <div className="flex justify-center items-center h-32">
          <ClipLoader color="#DAA520" size={30} />
        </div>
      ) : error ? (
        <div className="text-center text-red-500 font-bold mt-4">
          Error: {error}
        </div>
      ) : partner?.user?.notes?.length > 0 ? (
        // الجدول
        <table className="min-w-full border border-gray-300 rounded-lg shadow-lg">
          <thead className="bg-gradient-to-r from-black to-[#DAA520] text-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-left text-sm font-bold border-b border-gray-400"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-100 transition-all">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-6 py-2 text-sm font-bold border-b border-gray-300"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-center font-bold mt-4">There are no notes</div>
      )}

      {/* زر لإضافة ملاحظة جديدة */}
      <div className="flex justify-center items-center mt-6">
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-[#DAA520] hover:bg-[#b8911a] text-black font-semibold py-2 px-4 border border-black rounded shadow-md"
        >
          + Neue Notiz
        </button>
      </div>

      {/* نافذة المودال */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-lg font-bold mb-4 text-center">
              Schreiben Sie bitte hier Ihre Notizen
            </h2>
            <div className="flex flex-col mb-4">
              <label className="font-medium">Titel:</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="bg-gray-200 px-4 py-2 rounded focus:outline-none"
              />
            </div>
            <div className="flex flex-col mb-4">
              <label className="font-medium">Text:</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="bg-gray-200 px-4 py-2 rounded h-24 focus:outline-none resize-none"
              ></textarea>
            </div>
            <div className="flex justify-center mt-4">
              <button
                onClick={handleAddOrUpdateComment}
                className="bg-[#DAA520] hover:bg-[#b8911a] text-black font-semibold py-2 px-4 border border-black rounded shadow-md"
              >
                {editableNoteId ? "Aktualisieren" : "Hinzufügen"}
              </button>
              <button
                onClick={resetModal}
                className="ml-4 bg-gray-500 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded shadow-md"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminNotizenPartner;